import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { DeleteConfirmDialog } from "../components/pipeline/DeleteConfirmDialog";
import {
  FileBarChart,
  BarChart3,
  Plus,
  Play,
  Trash2,
  Download,
  Calendar,
  TrendingUp,
  Users,
  Wrench,
  AlertTriangle,
  ClipboardList,
  FileSpreadsheet,
  Eye,
  RefreshCw,
  Pencil,
  Search,
  Filter,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const reportTypeConfig = {
  VENTAS: { label: "Ventas", icon: TrendingUp, color: "bg-[#42E695]" },
  LEADS: { label: "Leads", icon: Users, color: "bg-[#03BEE4]" },
  EJECUTIVO: { label: "Por Ejecutivo", icon: Users, color: "bg-purple-500" },
  TECNICO: { label: "Técnico", icon: Wrench, color: "bg-orange-500" },
  INCIDENCIAS: { label: "Incidencias", icon: AlertTriangle, color: "bg-red-500" },
  SOLICITUDES: { label: "Solicitudes", icon: ClipboardList, color: "bg-blue-500" },
  PERSONALIZADO: { label: "Personalizado", icon: FileSpreadsheet, color: "bg-slate-500" },
};

// Status vocabularies (mirror backend enums).
const OPPORTUNITY_STAGES = [
  { value: "LEAD", label: "Lead" },
  { value: "CONTACTADO", label: "Contactado" },
  { value: "PROPUESTA", label: "Propuesta" },
  { value: "NEGOCIACION", label: "Negociación" },
  { value: "GANADA", label: "Ganada" },
  { value: "PERDIDA", label: "Perdida" },
];
const TICKET_STATUSES = [
  { value: "NUEVA", label: "Nueva" },
  { value: "EN_TRATAMIENTO", label: "En tratamiento" },
  { value: "PENDIENTE_PROVEEDOR", label: "Pendiente proveedor" },
  { value: "PENDIENTE_CLIENTE", label: "Pendiente cliente" },
  { value: "RESUELTO", label: "Resuelto" },
  { value: "CERRADO", label: "Cerrado" },
];
const PRIORITIES = [
  { value: "BAJA", label: "Baja" },
  { value: "MEDIA", label: "Media" },
  { value: "ALTA", label: "Alta" },
  { value: "CRITICA", label: "Crítica" },
];
const PROVISION_STATUSES = [
  { value: "PENDIENTE_COMERCIAL", label: "Pendiente comercial" },
  { value: "PENDIENTE_ADMINISTRATIVA", label: "Pendiente administrativa" },
  { value: "PENDIENTE_TECNICA", label: "Pendiente técnica" },
  { value: "COMPLETADA", label: "Completada" },
];

// Which contextual filters each report type exposes.
// key `stage`/`status`/`priority` render multi-toggle chips; `user` renders a
// single-select of users (with "Todos"). All are stored under report.filters.
const FILTER_SCHEMA = {
  VENTAS: { stage: { label: "Estados de venta", options: OPPORTUNITY_STAGES }, user: { label: "Ejecutivo" } },
  LEADS: { stage: { label: "Etapas del pipeline", options: OPPORTUNITY_STAGES }, user: { label: "Ejecutivo" } },
  EJECUTIVO: { stage: { label: "Estados", options: OPPORTUNITY_STAGES }, user: { label: "Ejecutivo" } },
  TECNICO: { status: { label: "Estados de provisión", options: PROVISION_STATUSES }, user: { label: "Técnico" } },
  INCIDENCIAS: {
    status: { label: "Estados", options: TICKET_STATUSES },
    priority: { label: "Prioridad", options: PRIORITIES },
    user: { label: "Asignado a" },
  },
  SOLICITUDES: { status: { label: "Estados", options: TICKET_STATUSES }, user: { label: "Asignado a" } },
  PERSONALIZADO: {},
};

// Result-table sanitisation helpers.
const HIDDEN_KEYS = new Set(["id"]);
const CURRENCY_KEYS = new Set(["total_amount", "total", "total_monthly", "estimated_mrr", "mrr", "importe", "amount"]);
const COLUMN_LABELS = {
  opportunity_id: "Referencia",
  provision_id: "Referencia",
  incident_number: "Nº Incidencia",
  request_number: "Nº Solicitud",
  title: "Título",
  customer_name: "Cliente",
  stage: "Etapa",
  status: "Estado",
  priority: "Prioridad",
  total_amount: "Importe",
  total: "Total",
  estimated_mrr: "MRR estimado",
  won_date: "Fecha de cierre",
  created_at: "Creado",
  resolved_at: "Resuelto",
  scheduled_date: "Fecha programada",
  ejecutivo_nombre: "Ejecutivo",
  tecnico_nombre: "Técnico",
  request_type: "Tipo",
  ejecutivo: "Ejecutivo",
  operaciones: "Operaciones",
};

const eurFormatter = new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" });
const isCurrencyKey = (k) => CURRENCY_KEYS.has(k);
const isDateKey = (k) => k.endsWith("_at") || k.endsWith("_date");
const formatCurrency = (v) => eurFormatter.format(Number(v) || 0);
const formatCellDate = (v) =>
  v ? new Date(v).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" }) : "-";
const humanizeHeader = (k) => COLUMN_LABELS[k] || k.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
const renderCell = (key, value) => {
  if (isCurrencyKey(key)) return formatCurrency(value);
  if (isDateKey(key)) return formatCellDate(value);
  if (typeof value === "number") return value.toLocaleString("es-ES");
  return value === null || value === undefined || value === "" ? "-" : String(value);
};

const ALL_USERS = "__ALL__";

const emptyForm = () => ({
  name: "",
  description: "",
  report_type: "VENTAS",
  date_range_start: "",
  date_range_end: "",
  filters: { status: [], stage: [], priority: [], user_id: "" },
});

// Multi-select toggle chips for status/stage/priority filters.
const FilterChips = ({ label, options, selected, onToggle, testidPrefix }) => (
  <div>
    <Label className="text-slate-300 flex items-center gap-1.5 mb-2">
      <Filter className="h-3.5 w-3.5 text-[#03BEE4]" />
      {label}
    </Label>
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const active = selected.includes(opt.value);
        return (
          <button
            type="button"
            key={opt.value}
            onClick={() => onToggle(opt.value)}
            data-testid={`${testidPrefix}-${opt.value}`}
            className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${
              active
                ? "bg-[#03BEE4] text-[#04042C] border-[#03BEE4]"
                : "bg-transparent text-slate-300 border-[#1E1E5F] hover:border-[#03BEE4]/50"
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  </div>
);

const ReportsPage = () => {
  const { canDelete } = useAuth();
  const [reports, setReports] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingReport, setEditingReport] = useState(null); // null = create mode
  const [reportForm, setReportForm] = useState(emptyForm());
  const [viewResultsOpen, setViewResultsOpen] = useState(false);
  const [reportResults, setReportResults] = useState(null);
  const [resultSearch, setResultSearch] = useState("");
  const [runningReport, setRunningReport] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, title: "", message: "", onConfirm: null });

  const fetchData = useCallback(async () => {
    try {
      const [reportsRes, templatesRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/api/reports`),
        axios.get(`${API_URL}/api/reports/templates/list`),
        axios.get(`${API_URL}/api/users`).catch(() => ({ data: [] })),
      ]);
      setReports(reportsRes.data);
      setTemplates(templatesRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error("Error fetching reports:", error);
      if (error.response?.status === 403) {
        toast.error("No tienes permisos para ver informes");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const schema = FILTER_SCHEMA[reportForm.report_type] || {};

  const openCreate = (typeId) => {
    setEditingReport(null);
    const type = typeId || "VENTAS";
    setReportForm({
      ...emptyForm(),
      report_type: type,
      name: typeId ? `${reportTypeConfig[type]?.label || type} - ${new Date().toLocaleDateString("es-ES")}` : "",
    });
    setFormOpen(true);
  };

  const openEdit = (report) => {
    setEditingReport(report);
    setReportForm({
      name: report.name || "",
      description: report.description || "",
      report_type: report.report_type || "VENTAS",
      date_range_start: report.date_range_start || "",
      date_range_end: report.date_range_end || "",
      filters: {
        status: report.filters?.status || [],
        stage: report.filters?.stage || [],
        priority: report.filters?.priority || [],
        user_id: report.filters?.user_id || "",
      },
    });
    setFormOpen(true);
  };

  const changeType = (v) => {
    // Reset contextual filters when the type changes to avoid stale keys.
    setReportForm((f) => ({ ...f, report_type: v, filters: { status: [], stage: [], priority: [], user_id: "" } }));
  };

  const toggleFilter = (key, value) => {
    setReportForm((f) => {
      const current = f.filters[key] || [];
      const updated = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
      return { ...f, filters: { ...f.filters, [key]: updated } };
    });
  };

  const setUserFilter = (v) => {
    setReportForm((f) => ({ ...f, filters: { ...f.filters, user_id: v === ALL_USERS ? "" : v } }));
  };

  const handleSaveReport = async () => {
    if (!reportForm.name.trim()) {
      toast.error("El nombre del informe es obligatorio");
      return;
    }
    try {
      if (editingReport) {
        await axios.put(`${API_URL}/api/reports/${editingReport.id}`, reportForm);
        toast.success("Informe actualizado correctamente");
      } else {
        await axios.post(`${API_URL}/api/reports`, reportForm);
        toast.success("Informe creado correctamente");
      }
      setFormOpen(false);
      setEditingReport(null);
      setReportForm(emptyForm());
      fetchData();
    } catch (error) {
      console.error("Error saving report:", error);
      toast.error(error.response?.data?.detail || "Error al guardar informe");
    }
  };

  const handleRunReport = async (reportId) => {
    setRunningReport(reportId);
    try {
      const response = await axios.post(`${API_URL}/api/reports/${reportId}/run`);
      setReportResults(response.data);
      setResultSearch("");
      setViewResultsOpen(true);
      fetchData();
      toast.success(`Informe ejecutado: ${response.data.result_count} resultados`);
    } catch (error) {
      console.error("Error running report:", error);
      toast.error("Error al ejecutar informe");
    } finally {
      setRunningReport(null);
    }
  };

  const handleDeleteReport = async (reportId, reportName) => {
    setDeleteConfirm({
      open: true,
      title: "Eliminar informe",
      message: `¿Estás seguro de eliminar el informe "${reportName}"?`,
      onConfirm: async () => {
        setDeleteConfirm((prev) => ({ ...prev, open: false }));
        try {
          await axios.delete(`${API_URL}/api/reports/${reportId}`);
          toast.success("Informe eliminado");
          fetchData();
        } catch (error) {
          console.error("Error deleting report:", error);
          toast.error("Error al eliminar informe");
        }
      },
    });
  };

  // Visible (non-hidden) columns for the current results.
  const visibleColumns = useMemo(() => {
    const rows = reportResults?.results || [];
    if (rows.length === 0) return [];
    return Object.keys(rows[0]).filter((k) => !HIDDEN_KEYS.has(k));
  }, [reportResults]);

  // Client-side free-text filtering of the results table.
  const filteredResults = useMemo(() => {
    const rows = reportResults?.results || [];
    const q = resultSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) =>
      visibleColumns.some((k) => String(row[k] ?? "").toLowerCase().includes(q))
    );
  }, [reportResults, resultSearch, visibleColumns]);

  const exportToCSV = () => {
    const rows = filteredResults;
    if (!rows || rows.length === 0) {
      toast.error("No hay datos para exportar");
      return;
    }
    const headers = visibleColumns;
    const csvContent = [
      headers.map((h) => humanizeHeader(h)).join(","),
      ...rows.map((row) =>
        headers
          .map((h) => {
            const val = isCurrencyKey(h) ? formatCurrency(row[h]) : isDateKey(h) ? formatCellDate(row[h]) : row[h] ?? "";
            return `"${String(val).replace(/"/g, '""')}"`;
          })
          .join(",")
      ),
    ].join("\n");
    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${(reportResults?.report_name || "informe").replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    toast.success("Informe exportado a CSV");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#42E695]"></div>
      </div>
    );
  }

  const activeFilterCount = (report) => {
    const f = report.filters || {};
    return (f.status?.length || 0) + (f.stage?.length || 0) + (f.priority?.length || 0) + (f.user_id ? 1 : 0);
  };

  return (
    <>
      <div className="space-y-6" data-testid="reports-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-heading font-semibold text-white flex items-center gap-3">
              <BarChart3 className="h-8 w-8 text-[#03BEE4]" />
              Informes
            </h1>
            <p className="text-slate-400 mt-1">Genera y gestiona informes de la plataforma</p>
          </div>
          <Button onClick={() => openCreate()} className="bg-[#42E695] text-[#04042C] hover:bg-[#34D399]" data-testid="create-report-btn">
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Informe
          </Button>
        </div>

        {/* Report Types Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {templates.map((template) => {
            const config = reportTypeConfig[template.id] || reportTypeConfig.PERSONALIZADO;
            const TypeIcon = config.icon;
            const reportCount = reports.filter((r) => r.report_type === template.id).length;
            return (
              <Card
                key={template.id}
                className="bg-[#0A0A35] border-[#1E1E5F] hover:border-[#42E695]/30 transition-all cursor-pointer"
                onClick={() => openCreate(template.id)}
                data-testid={`report-type-card-${template.id}`}
              >
                <CardContent className="pt-4 pb-4 text-center">
                  <div className={`w-10 h-10 rounded-lg ${config.color}/20 flex items-center justify-center mx-auto mb-2`}>
                    <TypeIcon className={`h-5 w-5 ${config.color.replace("bg-", "text-")}`} />
                  </div>
                  <p className="text-white text-sm font-medium">{config.label}</p>
                  <p className="text-slate-500 text-xs">{reportCount} informes</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Reports List */}
        <Card className="bg-[#0A0A35] border-[#1E1E5F]">
          <CardHeader>
            <CardTitle className="text-white font-heading flex items-center gap-2">
              <FileBarChart className="h-5 w-5 text-[#03BEE4]" />
              Mis Informes
            </CardTitle>
            <CardDescription className="text-slate-400">
              Informes guardados que puedes ejecutar en cualquier momento
            </CardDescription>
          </CardHeader>
          <CardContent>
            {reports.length === 0 ? (
              <div className="p-8 text-center border border-dashed border-[#1E1E5F] rounded-lg">
                <FileBarChart className="h-12 w-12 text-slate-500 mx-auto mb-4" />
                <p className="text-slate-400 mb-2">No hay informes creados</p>
                <p className="text-slate-500 text-sm">Crea tu primer informe para empezar a analizar datos</p>
              </div>
            ) : (
              <div className="space-y-3">
                {reports.map((report) => {
                  const config = reportTypeConfig[report.report_type] || reportTypeConfig.PERSONALIZADO;
                  const TypeIcon = config.icon;
                  const filterCount = activeFilterCount(report);
                  return (
                    <div
                      key={report.id}
                      className="p-4 rounded-lg bg-[#1B1B4B]/50 border border-[#1E1E5F] hover:border-[#42E695]/30 transition-all"
                      data-testid={`report-item-${report.id}`}
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 flex-1">
                          <div className={`p-2 rounded-lg ${config.color}/20`}>
                            <TypeIcon className={`h-5 w-5 ${config.color.replace("bg-", "text-")}`} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="text-white font-medium">{report.name}</h4>
                              <Badge className={`${config.color} text-white text-xs`}>{config.label}</Badge>
                              {filterCount > 0 && (
                                <Badge className="bg-[#1B1B4B] text-[#03BEE4] border border-[#03BEE4]/30 text-xs">
                                  <Filter className="h-3 w-3 mr-1" />
                                  {filterCount} filtro{filterCount > 1 ? "s" : ""}
                                </Badge>
                              )}
                            </div>
                            {report.description && <p className="text-slate-400 text-sm mt-1">{report.description}</p>}
                            <div className="flex items-center gap-4 mt-2 text-xs text-slate-500 flex-wrap">
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                Creado: {formatDate(report.created_at)}
                              </span>
                              {report.last_run_at && (
                                <span className="flex items-center gap-1">
                                  <RefreshCw className="h-3 w-3" />
                                  Última ejecución: {formatDate(report.last_run_at)} ({report.result_count} resultados)
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleRunReport(report.id)}
                            disabled={runningReport === report.id}
                            className="bg-[#42E695] text-[#04042C] hover:bg-[#34D399]"
                            data-testid={`run-report-${report.id}`}
                          >
                            {runningReport === report.id ? (
                              <RefreshCw className="h-4 w-4 animate-spin" />
                            ) : (
                              <Play className="h-4 w-4" />
                            )}
                            <span className="ml-1 hidden sm:inline">Ejecutar</span>
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEdit(report)}
                            className="text-[#03BEE4] hover:text-[#03BEE4] hover:bg-[#03BEE4]/10"
                            data-testid={`edit-report-${report.id}`}
                            title="Editar parámetros"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {canDelete("reports") && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteReport(report.id, report.name)}
                              className="text-red-400 hover:text-red-300"
                              data-testid={`delete-report-${report.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create / Edit Report Form Dialog */}
        <Dialog open={formOpen} onOpenChange={(o) => { if (!o) { setFormOpen(false); setEditingReport(null); } }}>
          <DialogContent className="bg-[#0A0A35] border-[#1E1E5F] text-white max-w-lg max-h-[88vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white font-heading flex items-center gap-2">
                <FileBarChart className="h-5 w-5 text-[#03BEE4]" />
                {editingReport ? "Editar Informe" : "Crear Nuevo Informe"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-slate-300">Nombre del Informe *</Label>
                <Input
                  value={reportForm.name}
                  onChange={(e) => setReportForm({ ...reportForm, name: e.target.value })}
                  placeholder="Ej: Ventas Q1 2026"
                  className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
                  data-testid="report-name-input"
                />
              </div>
              <div>
                <Label className="text-slate-300">Descripción</Label>
                <Textarea
                  value={reportForm.description}
                  onChange={(e) => setReportForm({ ...reportForm, description: e.target.value })}
                  placeholder="Descripción opcional del informe..."
                  rows={2}
                  className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
                />
              </div>
              <div>
                <Label className="text-slate-300">Tipo de Informe</Label>
                <Select value={reportForm.report_type} onValueChange={changeType}>
                  <SelectTrigger className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white" data-testid="report-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                    {templates.map((t) => (
                      <SelectItem key={t.id} value={t.id} className="text-white" data-testid={`report-type-opt-${t.id}`}>
                        <div className="flex flex-col">
                          <span>{t.name}</span>
                          <span className="text-xs text-slate-400">{t.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Contextual filters */}
              {(schema.stage || schema.status || schema.priority || schema.user) && (
                <div className="space-y-4 p-3 rounded-lg bg-[#1B1B4B]/40 border border-[#1E1E5F]" data-testid="contextual-filters">
                  {schema.stage && (
                    <FilterChips
                      label={schema.stage.label}
                      options={schema.stage.options}
                      selected={reportForm.filters.stage}
                      onToggle={(v) => toggleFilter("stage", v)}
                      testidPrefix="filter-stage"
                    />
                  )}
                  {schema.status && (
                    <FilterChips
                      label={schema.status.label}
                      options={schema.status.options}
                      selected={reportForm.filters.status}
                      onToggle={(v) => toggleFilter("status", v)}
                      testidPrefix="filter-status"
                    />
                  )}
                  {schema.priority && (
                    <FilterChips
                      label={schema.priority.label}
                      options={schema.priority.options}
                      selected={reportForm.filters.priority}
                      onToggle={(v) => toggleFilter("priority", v)}
                      testidPrefix="filter-priority"
                    />
                  )}
                  {schema.user && (
                    <div>
                      <Label className="text-slate-300 flex items-center gap-1.5 mb-2">
                        <Users className="h-3.5 w-3.5 text-[#03BEE4]" />
                        {schema.user.label}
                      </Label>
                      <Select value={reportForm.filters.user_id || ALL_USERS} onValueChange={setUserFilter}>
                        <SelectTrigger className="bg-[#0A0A35] border-[#1E1E5F] text-white" data-testid="filter-user-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0A0A35] border-[#1E1E5F] max-h-64">
                          <SelectItem value={ALL_USERS} className="text-white" data-testid="filter-user-all">
                            Todos
                          </SelectItem>
                          {users.map((u) => (
                            <SelectItem key={u.id} value={u.id} className="text-white">
                              {u.name} {u.email ? `(${u.email})` : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Fecha Inicio</Label>
                  <Input
                    type="date"
                    value={reportForm.date_range_start}
                    onChange={(e) => setReportForm({ ...reportForm, date_range_start: e.target.value })}
                    className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
                    data-testid="report-date-start"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Fecha Fin</Label>
                  <Input
                    type="date"
                    value={reportForm.date_range_end}
                    onChange={(e) => setReportForm({ ...reportForm, date_range_end: e.target.value })}
                    className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
                    data-testid="report-date-end"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-[#1E1E5F]">
                <Button variant="outline" onClick={() => { setFormOpen(false); setEditingReport(null); }} className="border-[#1E1E5F] text-slate-300">
                  Cancelar
                </Button>
                <Button onClick={handleSaveReport} className="bg-[#42E695] text-[#04042C]" data-testid="save-report-btn">
                  {editingReport ? "Guardar Cambios" : "Crear Informe"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Results Dialog */}
        <Dialog open={viewResultsOpen} onOpenChange={setViewResultsOpen}>
          <DialogContent className="bg-[#0A0A35] border-[#1E1E5F] text-white max-w-5xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white font-heading flex items-center justify-between gap-4">
                <span className="flex items-center gap-2">
                  <Eye className="h-5 w-5 text-[#03BEE4]" />
                  Resultados: {reportResults?.report_name}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={exportToCSV}
                  className="border-[#42E695] text-[#42E695] hover:bg-[#42E695]/20"
                  data-testid="export-csv-btn"
                >
                  <Download className="h-4 w-4 mr-1" />
                  Exportar CSV
                </Button>
              </DialogTitle>
            </DialogHeader>
            <div className="mt-4">
              <div className="flex items-center gap-4 mb-4 text-sm text-slate-400 flex-wrap">
                <span>Tipo: {reportResults?.report_type}</span>
                <span>Ejecutado: {formatDate(reportResults?.run_at)}</span>
                <span className="text-[#42E695]">{reportResults?.result_count} resultados</span>
              </div>

              {reportResults?.results?.length > 0 && (
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input
                    value={resultSearch}
                    onChange={(e) => setResultSearch(e.target.value)}
                    placeholder="Buscar en los resultados..."
                    className="pl-9 bg-[#0A0A35] border-[#1E1E5F] text-white"
                    data-testid="results-search-input"
                  />
                </div>
              )}

              {reportResults?.results?.length > 0 ? (
                <div className="rounded-lg border border-[#1E1E5F] overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[#1B1B4B] border-[#1E1E5F]">
                        {visibleColumns.map((key) => (
                          <TableHead key={key} className="text-slate-300 font-medium whitespace-nowrap">
                            {humanizeHeader(key)}
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredResults.slice(0, 100).map((row, idx) => (
                        <TableRow key={idx} className="border-[#1E1E5F] hover:bg-[#1B1B4B]/50" data-testid={`result-row-${idx}`}>
                          {visibleColumns.map((key) => (
                            <TableCell
                              key={key}
                              className={`text-white whitespace-nowrap ${isCurrencyKey(key) ? "font-medium text-[#42E695]" : ""}`}
                            >
                              {renderCell(key, row[key])}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {filteredResults.length === 0 && (
                    <div className="p-6 text-center text-slate-400 text-sm">
                      Sin coincidencias para "{resultSearch}"
                    </div>
                  )}
                  {filteredResults.length > 100 && (
                    <div className="p-3 text-center text-slate-400 text-sm bg-[#1B1B4B]/30">
                      Mostrando 100 de {filteredResults.length} resultados. Exporta a CSV para ver todos.
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-8 text-center border border-dashed border-[#1E1E5F] rounded-lg">
                  <FileBarChart className="h-12 w-12 text-slate-500 mx-auto mb-4" />
                  <p className="text-slate-400">No se encontraron resultados para este informe</p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <DeleteConfirmDialog
        open={deleteConfirm.open}
        onOpenChange={(open) => !open && setDeleteConfirm((prev) => ({ ...prev, open: false }))}
        onConfirm={deleteConfirm.onConfirm}
        onCancel={() => setDeleteConfirm((prev) => ({ ...prev, open: false }))}
        title={deleteConfirm.title}
        message={deleteConfirm.message}
      />
    </>
  );
};

export default ReportsPage;
